/** @format */
import { useTranslation } from 'next-i18next';
import React, { FunctionComponent, useState } from 'react';
import styled from 'styled-components';

import {
    CollapsibleAside,
    CollapsibleAsideTitlePosition,
    CollapsibleAsideVariant,
} from '@toucan/client/blocks/components/collapsible-aside';

import { ProductDetailsListItem } from '../product-details-list-item';
import { DEFAULT_TEST_ID, INITIAL_SHOWN_COUNT } from './constants';
import { styles } from './product-price-modifiers.styles';
import {
    ProductPriceModifiersProps,
    StyledProductPriceModifiersProps,
} from './types';

export const StyledProductPriceModifiers = styled(
    ProductDetailsListItem
)<StyledProductPriceModifiersProps>`
    ${styles}
`;

export const ProductPriceModifiers: FunctionComponent<
    ProductPriceModifiersProps
> = ({
    'data-testid': dataTestId = DEFAULT_TEST_ID,
    priceModifiers,
    icon,
    title,
    className = 'product-price-modifiers',
    ...otherProps
}) => {
    const { t } = useTranslation();
    const [isToggled, setIsToggled] = useState(false);

    const initialPriceModifiers = priceModifiers.slice(0, INITIAL_SHOWN_COUNT);
    const additionalPriceModifiers = priceModifiers.slice(INITIAL_SHOWN_COUNT);
    const isExpandable = additionalPriceModifiers.length > 0;

    return (
        <StyledProductPriceModifiers
            {...otherProps}
            data-testid={dataTestId}
            icon={icon}
            title={<strong>{title}</strong>}
            className={className}
        >
            <ul
                data-testid={`${dataTestId}__initial-price-modifiers`}
                className="price-modifiers__list"
            >
                {initialPriceModifiers.map(({ name, id }) => (
                    <li
                        key={id}
                        data-testid={`${dataTestId}__initial-price-modifier`}
                    >
                        {name}
                    </li>
                ))}
            </ul>
            {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
            {isExpandable && (
                <CollapsibleAside
                    variant={CollapsibleAsideVariant.INLINE}
                    data-testid={`${dataTestId}__additional-price-modifiers`}
                    className="additional-price-modifiers__list"
                    title={isToggled ? t('See less') : t('See more')}
                    titlePosition={CollapsibleAsideTitlePosition.BOTTOM}
                    onClick={() => setIsToggled(!isToggled)}
                >
                    <ul>
                        {additionalPriceModifiers.map(({ name, id }) => (
                            <li
                                key={id}
                                data-testid={`${dataTestId}__additional-price-modifier`}
                            >
                                {name}
                            </li>
                        ))}
                    </ul>
                </CollapsibleAside>
            )}
        </StyledProductPriceModifiers>
    );
};
